import React, { useState, useEffect } from 'react';
import TemplateHeader from './TemplateHeader';
import WidgetCustomizationModal from './WidgetCustomizationModal';
import TemplateDashboardService from '../api';

const ClientTemplateSummary = ({ 
  widgetContents = {}, 
  clientData = null, 
  useCaseId, 
  agentId, 
  domainId,
  isLoading = false
}) => {
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [widgetSettings, setWidgetSettings] = useState(() => {
    const defaultSettings = {
      summaryPrompt: "Generate a concise summary of the client's portfolio and key insights. Provide a short one-sentence summary and a detailed analysis limited to 200 words. Use plain text only with no formatting.",
      headerColor: "#ffffff",
      textColor: "#000000"
    };
    
    const savedSettings = localStorage.getItem('clientTemplateSummarySettings');
    
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        return { ...defaultSettings, ...parsed };
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ shortSummary: "", longSummary: "" });
  const [hasFetchedSummary, setHasFetchedSummary] = useState(false);

  const fetchSummary = async (forceRefresh = false) => {
    if (!widgetContents || Object.keys(widgetContents).length === 0) {
      setError("No widget content available to generate summary");
      return;
    }

    if (!clientData) {
      setError("No client data available");
      return;
    }
    
    // Skip if we've already fetched and not forcing refresh
    if (hasFetchedSummary && !forceRefresh) {
      return;
    }

    setSummaryLoading(true);
    setError(null);

    try {
      // Format widget contents into a single string
      const formattedContent = Object.entries(widgetContents)
        .map(([widgetId, content]) => {
          if (typeof content === 'string') {
            return content;
          } else if (content && typeof content === 'object') {
            return JSON.stringify(content);
          }
          return '';
        })
        .join('\n\n');

      const result = await TemplateDashboardService.generateClientSummary(
        formattedContent,
        clientData.name || clientData.id,
        widgetSettings.summaryPrompt,
        useCaseId,
        agentId,
        domainId
      );

      if (result.success && result.data) {
        setSummary(result.data);
        setHasFetchedSummary(true);
      } else {
        setError(result.error || "Failed to generate summary");
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
      setError("An error occurred while generating the summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  // Only fetch summary when widget contents are ready and not loading
  useEffect(() => {
    if (!isLoading && clientData && Object.keys(widgetContents).length > 0) {
      fetchSummary();
    }
  }, [isLoading, clientData, widgetContents]);

  // Reset summary when client changes
  useEffect(() => {
    if (clientData) {
      setHasFetchedSummary(false);
      setSummary({ shortSummary: "", longSummary: "" });
    }
  }, [clientData]);

  useEffect(() => {
    localStorage.setItem('clientTemplateSummarySettings', JSON.stringify(widgetSettings));
  }, [widgetSettings]);

  const handleRefresh = () => {
    fetchSummary(true);
  };

  const handleSaveSettings = (newSettings) => {
    const promptChanged = newSettings.summaryPrompt !== widgetSettings.summaryPrompt;
    setWidgetSettings(newSettings);

    if (promptChanged && Object.keys(widgetContents).length > 0) {
      setTimeout(() => {
        fetchSummary(true);
      }, 300);
    }
  };

  const shortSummary = summary?.shortSummary || "";
  const longSummary = summary?.longSummary || "";

  return (
    <div className="bg-gray-50 p-4">
      <div className="max-w-full mx-auto">
        <TemplateHeader
          shortSummary={shortSummary}
          longSummary={longSummary}
          isLoading={isLoading || summaryLoading}
          onRefresh={handleRefresh}
          widgetSettings={widgetSettings}
          onEditWidget={() => setShowCustomizationModal(true)}
        />
        
        {error && (
          <div className="text-error text-xs my-1 p-1 bg-error-light rounded text-center">
            {error}
          </div>
        )}
      </div>

      {/* Widget Customization Modal */}
      <WidgetCustomizationModal
        isOpen={showCustomizationModal}
        onClose={() => setShowCustomizationModal(false)}
        currentSettings={widgetSettings}
        onSave={handleSaveSettings}
        templateMode={true}
      />
    </div>
  );
};

export default ClientTemplateSummary;